/**
 * Shop and stylist photos are served from salunbd.com/img/... instead of straight from Cloudinary, so visitors only ever
 * see our own address, and so only the few sizes the site really uses can be asked for (nobody can run up the Cloudinary
 * bill by requesting a photo in a million different widths).
 *
 *   /img/<transformation>/<version>/<folder>/<file>.jpg   ->   https://res.cloudinary.com/<cloud>/image/upload/<the same>
 *
 * Plain TypeScript with no framework imports, so the route and the browser app share it.
 */

/** The widths the site asks for (customer/shims/cloudinary.ts rounds up to one of these). */
export const IMAGE_WIDTHS = [160, 240, 360, 480, 640, 900, 1280] as const;

const TRANSFORMATION = new RegExp(`^f_auto,q_auto(?:,c_limit,w_(?:${IMAGE_WIDTHS.join('|')}))?$`);
// A version, then folders and a file name made only of safe characters. No dots except the extension: no "..", no encoded slashes.
const ASSET_PATH = /^v\d{1,12}\/[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)*\.(?:jpg|jpeg|png|webp|avif|gif)$/i;

/** The Cloudinary address behind a /img/... path, or null when it is not a photo the site may serve. */
export function upstreamImageUrl(cloud: string, segments: string[]): string | null {
  if (!cloud || !/^[A-Za-z0-9_-]+$/.test(cloud) || segments.length < 3) return null;
  const [transformation, ...rest] = segments;
  const path = rest.join('/');
  if (!TRANSFORMATION.test(transformation) || !ASSET_PATH.test(path)) return null;
  return `https://res.cloudinary.com/${cloud}/image/upload/${transformation}/${path}`;
}

/** The /img/... address for a Cloudinary photo URL the site would have used, or null to leave the URL as it is. */
export function proxiedImagePath(url: string, cloud: string | undefined): string | null {
  if (!cloud) return null;
  const prefix = `https://res.cloudinary.com/${cloud}/image/upload/`;
  if (!url.startsWith(prefix)) return null;
  const rest = url.slice(prefix.length);
  return upstreamImageUrl(cloud, rest.split('/')) ? `/img/${rest}` : null;
}

const OWN_HOSTS = /^(?:(?:www\.)?salunbd\.com|localhost|127\.0\.0\.1|[a-z0-9-]+\.workers\.dev)$/i;

/**
 * Other websites may not borrow our photos. A request with no Referer (privacy settings, direct visits, app webviews) is
 * allowed: blocking those would break real visitors. This turns away casual hotlinking, not a determined scraper.
 */
export function referrerAllowed(referer: string | null, requestHost: string | null): boolean {
  if (!referer) return true;
  try {
    const host = new URL(referer).hostname;
    return OWN_HOSTS.test(host) || (!!requestHost && host === requestHost.split(':')[0]);
  } catch {
    return false;
  }
}
